import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {UsersService} from '../../../services/users/users.service';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {Dictionary} from 'highcharts/highcharts.src';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {AppSettings} from '../../../app.settings';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {FormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {Session} from 'protractor';
import {SessionService} from '../../../services/session/session.service';
import {SpinnerService} from '../../../services/spinner/spinner.service';
import {UtilsService} from '../../../services/utils/utils.service';
import { MatDialog } from '@angular/material/dialog';
import { PlaidSandboxToolDialogComponent } from './plaid-sandbox-tool-dialog.component';

interface UserBrandHistoryItem {
  brandId: string;
  joinedAt: string;
  leftAt: string | null;
  brandName: string;
  brandLogoUrl: string;
}

@Component({
    selector: 'app-manageusers',
    templateUrl: './manageusers.component.html',
    styleUrls: ['./manageusers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageusersComponent implements OnInit, AfterViewInit {

  @ViewChild('search') search: ElementRef | undefined;
  @ViewChild(MatSort) sort!: MatSort;

  public loading = false;
  private pageNumber = 0;
  public items = new MatTableDataSource<any>();
  public columnsToDisplay = ['createdAt', 'partnerImage', 'firstName', 'lastName', 'email', 'phoneNumber', 'address1', 'city', 'state', 'postalCode', 'signedUpBy', 'action'];
  public iFrameSource: SafeUrl | undefined;
  public iFrameTitle = '';
  public partners = Array<Dictionary<any>>();
  public brands = Array<Dictionary<any>>();
  public form;
  private partner = '';
  private atEnd = false;
  private appWindow: any;
  public isSuperAdmin = false;
  public plaidSandboxToolsEnabled = AppSettings.isQa || AppSettings.isLocal || AppSettings.apiParam === 'local';

  constructor(
      private usersService: UsersService,
      private domSanitizer: DomSanitizer,
      private uiAlertService: UiAlertService,
      private utilsService: UtilsService,
      private formBuilder: FormBuilder,
      private snackBar: MatSnackBar,
      private sessionService: SessionService,
      private spinnerService: SpinnerService,
      private dialog: MatDialog,
  ) {
    this.form = this.formBuilder.group({
      partner: new UntypedFormControl(0, [Validators.required]),
    });
  }

  ngOnInit(): void {
    let firstTime = true;
    this.usersService.meSubscribe(async (response: any) => {
      if (response && firstTime) {
        firstTime = false;
        try {
          this.brands = await this.usersService.getBrands();
          const partners = await  this.usersService.getVisibleBrands();
          this.partners = partners;
        } catch (err) {
          console.error('Error: ', err);
        }
        this.loadMoreItems();
        this.form.get('partner')?.valueChanges.subscribe(value => {
          this.partner = value ? this.partners[value - 1].partnerId : '';
          this.loadMoreItems(true);
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.items.sort = this.sort;
    fromEvent(this.search?.nativeElement, 'keyup').pipe(
            filter(Boolean),
            debounceTime(300),
            distinctUntilChanged(),
            tap((): void => {
              this.loadMoreItems(true);
            })
    ).subscribe();
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;

    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadMoreItems();
    }
  }

  loadMoreItems(clear = false): void {
    if (clear) {
      this.atEnd = false;
      this.pageNumber = 0;
      this.items.data = [];
    }
    if (this.loading || this.atEnd) {
      return;
    }
    this.loading = true;
    const page = this.pageNumber;
    this.isSuperAdmin = this.sessionService.isSuperAdmin();
    // An unrestricted All selection must also include users without a brand.
    const partnerFilter = this.partner || (this.isSuperAdmin
      ? ''
      : this.partners.map(partner => partner.partnerId).join(','));
    this.usersService.getUsers(this.search?.nativeElement.value, false, partnerFilter, page, 20).then(users => {
      this.loading = false;
      if (users.length === 0) {
        this.atEnd = true;
      } else {
        this.pageNumber++;
      }
      this.items.data = this.items.data.concat(users);
      this.items.data.forEach(item => {
        this.setItemExtraInfo(item);
      });
    }).catch(err => {
      console.error('Error in get users', err);
      this.pageNumber = 0;
      this.items.data = [];
    });
  }

  private setItemExtraInfo(item: any): void {
    const partner = this.partners.find(thisPartner => thisPartner.partnerId === item.partnerId);
    item.partnerImage = partner && partner.logoUrl ? partner.logoUrl : null;
    item.partnerName = partner?.name || '---';
    item.signedUpBy = item.signupUserId ? item.signupUserId === item.id ? 'Self' : 'Agent' : 'Unknown';
    item.brandHistoryItems = this.decorateBrandHistory(item);
  }

  getBrandHistory(user: any): UserBrandHistoryItem[] {
    return user.brandHistoryItems || [];
  }

  onBrandHistoryLogoError(historyItem: UserBrandHistoryItem): void {
    historyItem.brandLogoUrl = '';
  }

  private decorateBrandHistory(user: any): UserBrandHistoryItem[] {
    if (!Array.isArray(user?.metadata?.brandHistory)) {
      return [];
    }

    return user.metadata.brandHistory.map((historyItem: any) => {
      const brandId = `${historyItem?.brandId || ''}`.trim();
      const brand = this.brands.find((item) => `${item?.partnerId || ''}`.trim() === brandId);
      return {
        brandId,
        joinedAt: historyItem?.joinedAt,
        leftAt: historyItem?.leftAt,
        brandName: brand?.name || '',
        brandLogoUrl: brand?.logoUrl || '',
      };
    });
  }

  userLogin(user: any): void {
    this.usersService.loginUserV3(user.id, AppSettings.SUPPORT_DEV_MODE, (url) => {
      this.iFrameSource = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
      this.iFrameTitle = `${user.firstName || ''} ${user.lastName || ''}`;
      if (user.email) {
        this.iFrameTitle += ` (${user.email})`;
      }
      this.appWindow = window.open(url, '_blank', 'width=400,height=800,left=100,top=100,menubar=false,location=false,toolbar=false,status=false');
    });
  }

  closeIframe(): void {
    if (this.appWindow) {
      this.appWindow.close();
      this.appWindow = undefined;
    }
    this.iFrameSource = '';
  }

  addUser(): void {
    this.uiAlertService.presentAddUser().then(result => {
      if (result) {
        this.loadMoreItems(true);
      }
    });
  }

  private refresh(): void {
    this.items.data = [...this.items.data];
  }

  disableUser(user: any): void {
    this.usersService.disableUserAccount(user.id).then(_ => {
      user.isActivatedUser = false;
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'User account has been disabled'});
      this.refresh();
    });
  }

  enableUser(user: any): void {
    this.usersService.enableUserAccount(user.id).then(_ => {
      user.isActivatedUser = true;
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'User account has been enabled'});
      this.refresh();
    });
  }

  setPassword(user: any): void {
    this.uiAlertService.presentFieldInput('Set User Password', 'Password').then((value) => {
      if (value) {
        this.usersService
          .setPassword(user.id, value)
          .then(() => {
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: 'Password successfully changed',
            });
          })
          .catch((e) => {
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: e.data?.error ?? 'Could not change password',
            });
          });
      }
    });
  }

  sendPasswordReset(user: any): void {
        this.usersService.sendPasswordReset(user.email).then(() => {
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Password reset email sent'});
        });
  }

  async changePartner(user: any): Promise<void> {
    const partnerId = await this.uiAlertService.changePartner(user.partnerId);
    if (!partnerId) {
      return;
    }

    await this.usersService.setPartner(user.id, partnerId);
    const updatedUser = await this.usersService.getUserById(user.id);
    Object.assign(user, updatedUser);
    this.setItemExtraInfo(user);
    this.refresh();
    this.snackBar.openFromComponent(SnackBarComponent, {data: 'Partner successfully changed'});
  }

  loginHistory(user: any): void {
    this.uiAlertService.showUserLogins(user);
  }

  signedUpByClicked(item: any): void {
    if (item.signupUserId && item.id !== item.signupUserId) {
      this.usersService.getUserById(item.signupUserId).then((user: any) => {
        this.uiAlertService.presentMessage(`Signed up by ${this.utilsService.getUserName(user)}`);
      });
    }
  }

  playHelpMovie(): void {
    this.uiAlertService.presentMovie('Managing Customers' , UiAlertService.content.MANAGING_CUSTOMERS);
  }

  userEdit(user: any): void {
    this.uiAlertService.presentAddUser(user).then(result => {
      if (result) {
        Object.assign(user, result);
      }
      this.loadMoreItems(true);
    });
  }

  editUserEmail(user: any): void {
    this.uiAlertService.presentEditUserEmail(user).then(() => {
      this.loadMoreItems(true);
    });
  }

  openPlaidSandboxTools(user: any): void {
    if (!this.plaidSandboxToolsEnabled) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Plaid Sandbox Tools are available in local and QA only'
      });
      return;
    }
    this.dialog.open(PlaidSandboxToolDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {user}
    });
  }
}
